import React from 'react';
import { useNavigate } from 'react-router-dom';
import parse from 'html-react-parser';
import { sanitizeHtml } from 'components/sanitizeHtml';
import { withAuth } from 'utils';
import useAuthStore from 'store/auth';

interface RenderMentionsProps {
	content: string;
	taggedUsers: { userId: string; userType: string; name: string }[];
	hashTags: { id: string; title: string }[];
	hasMore?: boolean;
	maxLength?: number;
}

const RenderMentions: React.FC<RenderMentionsProps> = ({
	content,
	taggedUsers,
	hashTags,
	hasMore = false,
	maxLength = 200,
}) => {
	const { token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const navigate = useNavigate();

	const handleNavigation = (path: string) => navigate(path);

	const mentionRegex = /@\[(.*?)\]\((\d+|new-[^\)]+)\)/g;
	const hashtagRegex = /#\[(.*?)\]\((\d+|new-[^\)]+)\)|#([^\s]+)/g;

	const findIdByName = (name: string) =>
		hashTags?.find((hashtag) => hashtag.title === name)?.id || null;

	// Escape HTML to avoid injection
	const escapedContent = content.replace(/</g, '&lt;').replace(/>/g, '&gt;');

	// Function to create clickable HTML
	const processContent = (input: string) =>
		input
			.replace(mentionRegex, (_, display, id) => {
				const user = taggedUsers?.find((u) => String(u.userId) === String(id));
				const path = user
					? user.userType === 'seeker'
						? `/seeker/profile/${id}`
						: `/company/profile/${id}`
					: '#';
				return `<span class="mention" data-path="${path}" role="link">${display}</span>`;
			})
			.replace(hashtagRegex, (_, display, id, plainTag) => {
				const tagName = display || plainTag;
				const tagId = id || findIdByName(tagName);
				const path =
					id && id.startsWith('new-')
						? `/hashtag/profile/${findIdByName(tagName) || id}`
						: `/hashtag/profile/${tagId}`;
				return `<span class="hashtag" data-path="${path}" role="link">#${tagName}</span>`;
			})
			.replace(/\n/g, '<br>');

	// Plain text for truncation length check
	const plainTextContent = escapedContent
		.replace(mentionRegex, (_, display) => display)
		.replace(hashtagRegex, (_, display, __, plainTag) => `#${display || plainTag}`);

	// Decide what to render with word-safe truncation
	let htmlContent: string;
	if (hasMore && plainTextContent.length > maxLength) {
		// Find the last space before maxLength to avoid splitting words
		let truncIndex = maxLength;
		if (plainTextContent[maxLength] && plainTextContent[maxLength] !== ' ') {
			// Find the last space before maxLength
			const lastSpace = plainTextContent.lastIndexOf(' ', maxLength);
			if (lastSpace > 0) {
				truncIndex = lastSpace;
			}
		}
		const truncated = plainTextContent.slice(0, truncIndex).replace(/\s+$/, '');
		htmlContent =
			processContent(truncated) +
			`…<span class="read-more" style="color:#ff7f05;">read more</span>`;
	} else {
		htmlContent = processContent(escapedContent);
	}

	// Handle clicks for both navigation and read more
	const handleClick = (e: React.MouseEvent) => {
		const target = e.target as HTMLElement;
		const path = target.getAttribute('data-path');

		if (target.classList.contains('read-more')) {
			// In a real app you could set state to expand here
			console.log('Read more clicked');
			return;
		}

		if (path) {
			e.stopPropagation();
			handleNavigation(path);
		}
	};

	return <span onClick={withAuth(token, handleClick)}>{parse(sanitizeHtml(htmlContent))}</span>;
};

export default RenderMentions;
